/**
 * Analytics helpers — GA4 (gtag.js) + Google Tag Manager share one
 * `dataLayer`, both installed in public/index.html.
 *
 * `track(event, params)` sends a GA4 event via gtag when it is loaded and
 * falls back to a plain dataLayer push (GTM "Custom Event" trigger) when it
 * is not. Either way analytics must never break the app, so everything is
 * wrapped in try/catch and undefined/empty params are dropped.
 *
 * `installGlobalClickTracking()` (mounted once in App.jsx) turns markup into
 * events with no per-component code:
 *   - any element with `data-track="event_name"` fires that event on click;
 *     extra `data-track-<key>="value"` attributes become event params
 *     (e.g. data-track-source="footer" -> { source: 'footer' })
 *   - `tel:` links       -> phone_click
 *   - wa.me / whatsapp   -> whatsapp_click
 *   - Play / App Store   -> app_download { platform }
 *   - `mailto:` links    -> email_click
 * Event names follow the client's GA4 plan (search_warehouse,
 * list_warehouse_click, warehouse_registration, customer_registration,
 * warehouse_enquiry, whatsapp_click, phone_click, app_download, plus the
 * GA4 marketplace set: view_item, generate_lead, add_to_wishlist, add_listing).
 */

function clean(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    out[k] = v;
  });
  return out;
}

export function track(event, params = {}) {
  try {
    if (typeof window === 'undefined' || !event) return;
    const payload = clean(params);
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload });
    }
  } catch {
    /* analytics must never break the app */
  }
}

const WHATSAPP_RE = /(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)|^whatsapp:/i;

function paramsFromDataset(el) {
  const out = {};
  try {
    Object.entries(el.dataset || {}).forEach(([k, v]) => {
      // dataset key "trackSource" (from data-track-source) -> "source"
      if (k !== 'track' && k.startsWith('track')) {
        const key = k.slice(5);
        out[key.charAt(0).toLowerCase() + key.slice(1)] = v;
      }
    });
  } catch { /* noop */ }
  return out;
}

function handleDocumentClick(e) {
  try {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const el = target.closest('[data-track], a[href]');
    if (!el) return;
    const page_path = window.location.pathname;
    if (el.dataset && el.dataset.track) {
      track(el.dataset.track, { ...paramsFromDataset(el), page_path });
      return;
    }
    const href = el.getAttribute('href') || '';
    if (/^tel:/i.test(href)) track('phone_click', { phone: href.slice(4).replace(/\s+/g, ''), page_path });
    else if (WHATSAPP_RE.test(href)) track('whatsapp_click', { source: 'link', page_path });
    else if (/play\.google\.com/i.test(href)) track('app_download', { platform: 'android', page_path });
    else if (/apps\.apple\.com|itunes\.apple\.com/i.test(href)) track('app_download', { platform: 'ios', page_path });
    else if (/^mailto:/i.test(href)) track('email_click', { page_path });
  } catch {
    /* noop */
  }
}

export function installGlobalClickTracking() {
  try {
    if (typeof document === 'undefined' || window.__ksClickTracking) return;
    window.__ksClickTracking = true;
    // Capture phase so we see the click even when the handler navigates away.
    document.addEventListener('click', handleDocumentClick, true);
  } catch {
    /* noop */
  }
}

export default track;
